import React, { useContext, useEffect, useState } from 'react';
import { DownOutlined, EllipsisOutlined, ReloadOutlined, SyncOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Tag, Typography } from 'antd';
import { apiClient, apiErrorMessage, hasPermission } from '../../../utils';
import { Role, User } from '../../../models';
import moment from 'moment';
import { EditUserRolesPage } from '.';
import { EntityChangeLog, LoginAttemptLog } from '../../reports';
import { UserContext } from '../../contexts';

export function UsersPage() {
  const { Content } = Layout;
  const { Text, Link } = Typography;

  const { user } = useContext(UserContext);

  const [editRolesModalOpen, setEditRolesModalOpen] = useState<undefined | 'add' | 'del'>();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>();
  const [selectedUsers, setSelectedUsers] = useState<User[]>();
  const [changeLogModalOpen, setChangeLogModalOpen] = useState<string>();
  const [loginAttemptLogModalOpen, setLoginAttemptLogModalOpen] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canAssignRoles, setCanAssignRoles] = useState(false);
  const [canViewLoginAttempts, setCanViewLoginAttempts] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setCanEdit(hasPermission(user, 'users.edit'));
    setCanAssignRoles(hasPermission(user, 'users.role_assign'));
    setCanViewLoginAttempts(hasPermission(user, 'users.view_login_attempts'));
  }, [user]);

  const loadUsers = () => {
    setLoading(true);
    apiClient
      .get('users')
      .then(response => {
        setUsers(response.data as User[]);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const syncWithSAP = () => {
    setLoading(true);
    apiClient
      .get('users/integration')
      .then(() => {
        loadUsers();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  const openEditRolesModal = (action: 'add' | 'del') => {
    setEditRolesModalOpen(action);
  };

  const closeEditRolesModal = () => {
    setEditRolesModalOpen(undefined);
  };

  const openChangeLogModal = (id: string) => {
    setChangeLogModalOpen(id);
  };

  const closeChangeLogModal = () => {
    setChangeLogModalOpen(undefined);
  };

  const openLoginAttemptLogModal = () => {
    setLoginAttemptLogModalOpen(true);
  };

  const closeLoginAttemptLogModal = () => {
    setLoginAttemptLogModalOpen(false);
  };

  const onRefreshClick = () => {
    loadUsers();
  };

  const onSyncClick = () => {
    syncWithSAP();
  };

  const onRolesUpdated = () => {
    closeEditRolesModal();
    loadUsers();
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'changelog') {
      openChangeLogModal(id);
    }
  };

  const onBulkActionClick = (e: any) => {
    if (e.key === 'addRoles') {
      openEditRolesModal('add');
    } else if (e.key === 'delRoles') {
      openEditRolesModal('del');
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: User[]) => {
      if (selectedRows.length) {
        setSelectedUsers(selectedRows);
      } else {
        setSelectedUsers(undefined);
      }
    },
  };

  const tableActions = (id: string) => (
    <Menu onClick={(e: any) => onTableActionClick(e, id)}>
      <Menu.Item key="changelog">
        <Text>Değişim geçmişi</Text>
      </Menu.Item>
    </Menu>
  );

  const bulkActionsMenu = (
    <Menu onClick={onBulkActionClick}>
      <Menu.Item key="addRoles">Rol ekle</Menu.Item>
      <Menu.Item key="delRoles">Rol sil</Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: 'Personel no',
      dataIndex: 'sourceId',
      key: 'sourceId',
      render: (sourceId: string) => <Text strong>{sourceId}</Text>,
    },
    {
      title: 'İsim',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (fullName: string) => <Text strong>{fullName}</Text>,
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => <Link href={'mailto:' + email}>{email}</Link>,
    },
    {
      title: 'Roller',
      key: 'roles',
      dataIndex: 'roles',
      render: (roles: Role[]) => (
        <>
          {roles.length === 0
            ? '-'
            : roles.map(role => (
                <Tag key={role.id || 'superadmin'} color={role.name === 'Superadmin' ? 'red' : 'default'}>
                  {role.name}
                </Tag>
              ))}
        </>
      ),
    },
    {
      title: 'Son giriş',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (lastLoginAt: string) => (lastLoginAt ? moment(lastLoginAt).format('DD.MM.yyyy HH:mm:ss') : '-'),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, user: User) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(user.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <UserOutlined /> Kullanıcılar
          </>
        }
        extra={[
          <>
            {canViewLoginAttempts && (
              <Button key="attempt" onClick={openLoginAttemptLogModal} type="link">
                Yetkisiz erişimler
              </Button>
            )}
          </>,
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
          <>
            {canAssignRoles && (
              <Dropdown key="bulk" overlay={bulkActionsMenu}>
                <Button>
                  Toplu işlemler <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </>,
          <>
            {canEdit && (
              <Button type="primary" key="sync" onClick={onSyncClick} icon={<SyncOutlined />}>
                SAP ile eşle
              </Button>
            )}
          </>,
        ]}
      />
      <Content>
        <Table
          columns={tableColumns}
          rowKey={row => row.id}
          dataSource={users}
          rowSelection={{ ...rowSelection }}
          loading={loading}
          pagination={false}
        />
      </Content>
      <Modal
        title={'Toplu rol ' + (editRolesModalOpen === 'add' ? 'ekle' : 'sil')}
        visible={editRolesModalOpen !== undefined}
        footer={null}
        onCancel={closeEditRolesModal}
        destroyOnClose={true}
      >
        <EditUserRolesPage onSaved={onRolesUpdated} action={editRolesModalOpen} selectedUsers={selectedUsers as User[]} />
      </Modal>
      <Modal
        title="Değişim geçmişi"
        visible={changeLogModalOpen !== undefined}
        footer={null}
        onCancel={closeChangeLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <EntityChangeLog entityName={'User'} recordId={changeLogModalOpen} />
      </Modal>
      <Modal
        title="Uygulamaya yetkisiz erişim denemeleri"
        visible={loginAttemptLogModalOpen}
        footer={null}
        onCancel={closeLoginAttemptLogModal}
        destroyOnClose={true}
        width={'80%'}
      >
        <LoginAttemptLog />
      </Modal>
    </>
  );
}
